<template>
  <div class="trend-card trend-card--chart">
    <transition name="trend-collapse" @after-enter="onAfterEnter">
      <div v-show="!collapsed" class="trend-card__body trend-card__body--chart">
        <div class="trend-loading" v-if="isLoading">Loading...</div>
        <div class="trend-empty" v-else-if="isTrendEmpty">
          {{ emptyText }}
        </div>

        <div v-else>
          <div class="uw-toolbar">
            <div class="uw-toolbar__left">
              <select class="uw-toolbar__select" v-model="timeframe">
                <option value="rolling90">Rolling 90</option>
              </select>

              <select class="uw-toolbar__select" v-model="uwToAddKey">
                <option value="">Select underwriters..</option>
                <option v-for="u in availableUwOptions" :key="u.key" :value="u.key">
                  {{ u.name }}
                </option>
              </select>
            </div>

            <div class="uw-toolbar__right">
              <div class="uw-pills" v-if="selectedUwKeys.length">
                <div class="uw-pill" v-for="k in selectedUwKeys" :key="k">
                  <span class="uw-pill__name">{{ byUw[k]?.name || "Unknown" }}</span>
                  <button class="uw-pill__x" type="button" @click="removeUw(k)">×</button>
                </div>
              </div>

              <button class="uw-clear" type="button" @click="clearUw" v-if="selectedUwKeys.length">
                Clear
              </button>
            </div>
          </div>

          <Chart
            :key="chartRenderKey"
            :style="{ width: '100%', height: '210px' }"
            :legend="{ visible: false }"
          >
            <ChartCategoryAxis>
              <ChartCategoryAxisItem
                :categories="categories"
                :major-grid-lines="{ visible: false }"
                :labels="{ rotation: -45 }"
              />
            </ChartCategoryAxis>

            <ChartValueAxis>
              <ChartValueAxisItem :min="0" :major-grid-lines="{ visible: true }" />
            </ChartValueAxis>

            <ChartTooltip :render="'tooltipTpl'">
              <template #tooltipTpl="{ props }">
                <div class="ci-tooltip">
                  <div class="ci-tooltip__date">{{ tooltipDateFromProps(props) }}</div>
                  <div class="ci-tooltip__value">
                    {{ Number(props?.point?.value ?? 0).toFixed(2) }}
                  </div>
                </div>
              </template>
            </ChartTooltip>

            <ChartSeries>
              <ChartSeriesItem
                v-for="s in chartSeries"
                :key="s.key"
                :type="s.type"
                :name="s.name"
                :data-items="s.dataItems"
                :markers="s.markers"
              />
            </ChartSeries>
          </Chart>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ApiService from "@/api/api.service.js"

import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartTooltip
} from "@progress/kendo-vue-charts"

export default {
  name: "InitialUwRollingChartCard",
  components: {
    Chart,
    ChartSeries,
    ChartSeriesItem,
    ChartCategoryAxis,
    ChartCategoryAxisItem,
    ChartValueAxis,
    ChartValueAxisItem,
    ChartTooltip
  },
  props: {
    azureId: { type: String, required: true },
    authToken: { type: String, required: true },
    orgId: { type: [Number, String], default: null },

    trendRows: { type: Array, default: null },
    loadingExternal: { type: Boolean, default: false },
    titleOverride: { type: String, default: "" },
    emptyOverride: { type: String, default: "" }
  },
  data() {
    return {
      loading: false,
      collapsed: false,
      chartRenderKey: 1,

      timeframe: "rolling90",

      categoriesFull: [],
      categories: [],

      dates: [],
      byUw: {},
      uwOptions: [],

      uwToAddKey: "",
      selectedUwKeys: [],

      avgPerDay: [],
      trendWeekly: [],
      dailyAggTotals: []
    }
  },
  computed: {
    isLoading() {
      return this.loadingExternal || this.loading
    },
    cardTitle() {
      return this.titleOverride || "Initial Underwrites (Rolling 90 days)"
    },
    emptyText() {
      return this.emptyOverride || "No initial underwrites in the last 90 days"
    },
    storageKey() {
      const org = this.orgId != null ? String(this.orgId) : "all"
      return `initialuw_rolling_chart_collapsed_${org}`
    },
    chartRange() {
      const end = new Date()
      end.setHours(23, 59, 59, 999)

      if (this.timeframe === "monthly") {
        const start = new Date(end)
        start.setMonth(start.getMonth() - 6)
        start.setDate(1)
        start.setHours(0, 0, 0, 0)
        return { start, end }
      }

      const start = new Date(end)
      start.setDate(start.getDate() - 89)
      start.setHours(0, 0, 0, 0)
      return { start, end }
    },
    labelStep() {
      const n = this.categoriesFull.length || 0
      if (n <= 14) return 1
      if (n <= 30) return 2
      if (n <= 60) return 4
      return 7
    },
    isTrendEmpty() {
      if (!this.dailyAggTotals.length) return true
      return this.dailyAggTotals.every(x => Number(x.initialEventCount || 0) === 0)
    },
    availableUwOptions() {
      const selected = new Set(this.selectedUwKeys)
      return this.uwOptions.filter(x => !selected.has(x.key))
    },
    chartSeries() {
      const series = []

   

      series.push({
        key: "trend_weekly",
        type: "line",
        name: "Weekly trend",
        dataItems: this.trendWeekly,
        markers: { visible: false }
      })

      for (const uwKey of this.selectedUwKeys) {
        const uw = this.byUw[uwKey]
        if (!uw) continue
        const dataItems = this.dates.map(d => Number(uw.map[d] || 0))
        series.push({
          key: `uw_${uwKey}`,
          type: "column",
          name: uw.name,
          dataItems
        })
      }

      return series
    }
  },
  methods: {
    tooltipDateFromProps(props) {
      const p = props?.point || {}
      const idxRaw = p.categoryIndex ?? p.categoryIx ?? p.categoryIdx
      const idx = Number.isInteger(idxRaw) ? idxRaw : -1

      const dateKey = idx >= 0 ? this.dates[idx] : ""
      if (dateKey) return this.fmtTooltipDate(dateKey)

      const cat = p.category
      if (!cat) return ""
      return String(cat).includes("-") ? this.fmtTooltipDate(String(cat)) : String(cat)
    },
    fmtTooltipDate(dateKey) {
      const parts = String(dateKey || "").split("-")
      if (parts.length !== 3) return String(dateKey || "")
      return `${parts[1]}/${parts[2]}/${parts[0]}`
    },
    _loadCollapsed() {
      try {
        const raw = localStorage.getItem(this.storageKey)
        if (!raw) return
        this.collapsed = raw === "1" || raw === "true"
      } catch {}
    },
    _saveCollapsed() {
      try {
        localStorage.setItem(this.storageKey, this.collapsed ? "1" : "0")
      } catch {}
    },
    toggleCollapsed() {
      const wasCollapsed = this.collapsed
      this.collapsed = !this.collapsed
      this._saveCollapsed()
      if (wasCollapsed && !this.collapsed) {
        this.forceChartRedraw()
      }
    },
    onAfterEnter() {
      this.forceChartRedraw()
    },
    forceChartRedraw() {
      this.$nextTick(() => {
        this.chartRenderKey += 1
      })
    },
    dateKeyLocal(v) {
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return ""
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      return `${yyyy}-${mm}-${dd}`
    },
    dayLabel(v) {
      const parts = String(v || "").split("-")
      if (parts.length !== 3) return ""
      return `${parts[1]}/${parts[2]}`
    },
    addUw() {
      const k = this.uwToAddKey
      if (!k) return
      if (!this.byUw[k]) return
      if (this.selectedUwKeys.includes(k)) {
        this.uwToAddKey = ""
        return
      }
      this.selectedUwKeys = [...this.selectedUwKeys, k]
      this.uwToAddKey = ""
    },
    removeUw(k) {
      this.selectedUwKeys = this.selectedUwKeys.filter(x => x !== k)
    },
    clearUw() {
      this.selectedUwKeys = []
    },
    weekStartKeyFromDateKey(dateKey) {
      const parts = String(dateKey || "").split("-")
      if (parts.length !== 3) return ""
      const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
      if (Number.isNaN(d.getTime())) return ""

      const day = d.getDay()
      const diffToMon = (day + 6) % 7
      d.setDate(d.getDate() - diffToMon)

      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      return `${yyyy}-${mm}-${dd}`
    },
    buildWeeklyAvgOfDailyAvg(dates, dailyValues) {
      const byWeek = {}

      for (let i = 0; i < dates.length; i += 1) {
        const d = dates[i]
        const v = Number(dailyValues[i] || 0)
        const wk = this.weekStartKeyFromDateKey(d)
        if (!wk) continue
        if (!byWeek[wk]) byWeek[wk] = { sum: 0, n: 0 }
        byWeek[wk].sum += v
        byWeek[wk].n += 1
      }

      return dates.map(d => {
        const wk = this.weekStartKeyFromDateKey(d)
        const row = wk ? byWeek[wk] : null
        return row && row.n ? row.sum / row.n : 0
      })
    },
    async fetchTrend() {
      if (!this.azureId || !this.authToken) return
      this.loading = true
      try {
        const { start, end } = this.chartRange
        const resp = await ApiService.GetUnderwrittenLoanStatsByUnderwriter(
          this.azureId,
          start,
          end,
          this.authToken,
          this.orgId
        )
        this.normalize(resp?.data || {})
      } finally {
        this.loading = false
      }
    },
    normalize(payload) {
      const data = payload?.data?.data || payload?.data || payload || {}
      const dailyTrend = Array.isArray(data.dailyTrend) ? data.dailyTrend : []

      const rows = [...dailyTrend]
        .map(x => ({
          underwriterAzureId: x.underwriterAzureId,
          underwriterDisplayName: x.underwriterDisplayName,
          dateValue: x.initialUwDate,
          initialEventCount: Number(x.initialEventCount || 0),
          initialDistinctLoans: Number(x.initialDistinctLoans || 0)
        }))
        .filter(x => x.dateValue)

      this.applyTrendRows(rows)

      if (!this.collapsed) {
        this.forceChartRedraw()
      }
    },
    applyTrendRows(rows) {
      const byUw = {}
      for (const r of rows || []) {
        const dateKey = this.dateKeyLocal(r.dateValue)
        const uwKey = r.underwriterAzureId || r.underwriterDisplayName || "unknown"
        if (!dateKey) continue

        if (!byUw[uwKey]) {
          byUw[uwKey] = { key: uwKey, name: r.underwriterDisplayName || "Unknown", map: {} }
        }
        byUw[uwKey].map[dateKey] = (byUw[uwKey].map[dateKey] || 0) + Number(r.initialEventCount || 0)
      }

      const effectiveRange = this.rangeFromRows(rows) || this.chartRange
      let dates = this.buildDateKeysInclusive(effectiveRange.start, effectiveRange.end)

      dates = dates.filter(d => {
        for (const k of Object.keys(byUw)) {
          if (Number(byUw[k].map[d] || 0) > 0) return true
        }
        return false
      })

      this.dates = dates

      this.categoriesFull = dates.map(d => this.dayLabel(d))
      const step = this.labelStep || 7
      this.categories = this.categoriesFull.map((label, idx) => (idx % step === 0 ? label : ""))

      const uwKeysWithAnyData = Object.keys(byUw).filter(k => {
        const uw = byUw[k]
        return dates.some(d => Number(uw.map[d] || 0) > 0)
      })

      this.byUw = byUw
      this.uwOptions = uwKeysWithAnyData
        .map(k => ({ key: k, name: byUw[k].name }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name)))

      this.uwToAddKey = ""
      this.selectedUwKeys = []

      this.avgPerDay = dates.map(d => {
        let sum = 0
        let n = 0
        for (const k of uwKeysWithAnyData) {
          const v = Number(byUw[k].map[d] || 0)
          if (v > 0) {
            sum += v
            n += 1
          }
        }
        return n ? sum / n : 0
      })

      this.trendWeekly = this.buildWeeklyAvgOfDailyAvg(this.dates, this.avgPerDay)

      this.dailyAggTotals = dates.map(d => {
        let sumEvents = 0
        for (const k of Object.keys(byUw)) sumEvents += Number(byUw[k].map[d] || 0)
        return { dateValue: d, initialEventCount: sumEvents }
      })
    },
    rangeFromRows(rows) {
      if (!Array.isArray(rows) || !rows.length) return null

      let min = null
      let max = null

      for (const r of rows) {
        if (!r?.dateValue) continue
        const d = new Date(r.dateValue)
        if (Number.isNaN(d.getTime())) continue
        d.setHours(0, 0, 0, 0)
        if (!min || d < min) min = new Date(d)
        if (!max || d > max) max = new Date(d)
      }

      if (!min || !max) return null

      const start = new Date(min)
      start.setHours(0, 0, 0, 0)

      const end = new Date(max)
      end.setHours(23, 59, 59, 999)

      return { start, end }
    },
    buildDateKeysInclusive(start, end) {
      const s = new Date(start)
      s.setHours(0, 0, 0, 0)
      const e = new Date(end)
      e.setHours(0, 0, 0, 0)

      const out = []
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const day = d.getDay()
        if (day === 0 || day === 6) continue

        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, "0")
        const dd = String(d.getDate()).padStart(2, "0")
        out.push(`${yyyy}-${mm}-${dd}`)
      }
      return out
    }
  },
  mounted() {
    this._loadCollapsed()

    if (Array.isArray(this.trendRows) && this.trendRows.length) {
      this.applyTrendRows(this.trendRows)
      if (!this.collapsed) {
        this.forceChartRedraw()
      }
      return
    }

    this.fetchTrend()

    if (!this.collapsed) {
      this.forceChartRedraw()
    }
  },
  watch: {
    timeframe() {
      if (this.trendRows) {
        this.applyTrendRows(this.trendRows)
        if (!this.collapsed) this.forceChartRedraw()
        return
      }
      this.fetchTrend()
    },
    uwToAddKey(v) {
      if (!v) return
      this.addUw()
      if (!this.collapsed) this.forceChartRedraw()
    },
    trendRows: {
      deep: true,
      handler(v) {
        if (Array.isArray(v)) {
          this.applyTrendRows(v)
          if (!this.collapsed) {
            this.forceChartRedraw()
          }
        }
      }
    },
    selectedUwKeys() {
      if (!this.collapsed) this.forceChartRedraw()
    },
    azureId() {
      if (!this.trendRows) this.fetchTrend()
    },
    authToken() {
      if (!this.trendRows) this.fetchTrend()
    },
    orgId() {
      this._loadCollapsed()
      if (!this.trendRows) this.fetchTrend()
    }
  }
}
</script>

<style scoped>
.trend-card {
  background: var(--background);
  border: 1px solid var(--border-ltr);
  border-radius: 0px;
  overflow: hidden;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.trend-card__body--chart {
  padding: 10px 10px 14px;
}

.trend-loading,
.trend-empty {
  height: 210px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: var(--text-secondary-on-background);
  gap: 10px;
}

.uw-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
  min-width: 0;
}

.uw-toolbar__left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
}

.uw-toolbar__select {
  width: 200px;
  height: 28px;
  border-radius: 10px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.03);
  padding: 4px 10px;
  font-weight: 850;
  color: rgba(15, 23, 42, 0.85);
}

.uw-toolbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}

.uw-pills {
  display: flex;
  gap: 8px;
  align-items: center;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  flex: 1 1 auto;
  min-width: 0;
  padding-bottom: 2px;
}

.uw-pills::-webkit-scrollbar {
  height: 6px;
}

.uw-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.04);
  font-weight: 900;
  color: rgba(15, 23, 42, 0.85);
  flex: 0 0 auto;
}

.uw-pill__name {
  font-size: 12px;
  line-height: 1;
}

.uw-pill__x {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.14);
  background: rgba(15, 23, 42, 0.06);
  cursor: pointer;
  font-weight: 950;
  line-height: 1;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.uw-clear {
  height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.03);
  font-weight: 950;
  color: rgba(15, 23, 42, 0.75);
  cursor: pointer;
  flex: 0 0 auto;
}

.trend-collapse-enter-active,
.trend-collapse-leave-active {
  transition: max-height 0.18s ease, opacity 0.18s ease;
  overflow: hidden;
}

.trend-collapse-enter-from,
.trend-collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.trend-collapse-enter-to,
.trend-collapse-leave-from {
  max-height: 1000px;
  opacity: 1;
}

.ci-tooltip {
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #fff;
  font-weight: 900;
  line-height: 1.15;
}

.ci-tooltip__date {
  font-weight: 950;
  font-size: 12px;
  opacity: 0.95;
}

.ci-tooltip__value {
  margin-top: 4px;
  font-size: 12px;
  font-weight: 900;
}
</style>
